import React from 'react';
import './AnswerAdSlot.scss';
import { useTranslation } from 'react-i18next';
import CustomTooltip from '../../../../../../../shared/components/common/tooltip/CustomTooltip';
import { MetadataBadge } from '../../../../../../../shared/components/metadata-badge/MetadataBadge';
import { useGetAnswerFeatures } from '../../../../../../../shared/api/hooks/monitor/features/useGetAnswerFeatures';
import { ResponseMetadata } from '../../../../../../../shared/models/monitor/ResponseFeatures';
import { getSourceHref } from '../../../../../../../shared/utils/urls';

interface AnswerAdSlotProps {
  monitorDataId: number;
  responseMetadata?: ResponseMetadata | null;
  /** Share of the period's answers containing ads (header badge). */
  adsPeriodPercent: number | null;
}

const domainOf = (url?: string): string => {
  if (!url) return '';
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
};

/**
 * Compact "ChatGPT Ads" card rendered in the RIGHT column of an expanded answer (below the brand
 * positions), passed to ChatResponse via its `adSlot`. Shares the lazily-fetched answer features
 * cache with AnswerProductsSlot (same monitorDataId query key), so no extra request is made.
 */
const AnswerAdSlot: React.FC<AnswerAdSlotProps> = ({
  monitorDataId,
  responseMetadata,
  adsPeriodPercent,
}) => {
  const { t } = useTranslation();
  const hasAds = (responseMetadata?.adsCount ?? 0) > 0;
  const { features } = useGetAnswerFeatures(hasAds ? monitorDataId : null);

  if (!hasAds || !features || features.ads.length === 0) {
    return null;
  }

  return (
    <div className="answer-ad-slot">
      <div className="answer-ad-slot__header">
        <span className="ico--ads ico--sm" />
        <span className="answer-ad-slot__title">{t('chatgptAds')}</span>
        <CustomTooltip tooltipText="chatgptAdsTooltip" addMargin={false} />
        {adsPeriodPercent !== null && (
          <MetadataBadge
            variant="secondary"
            text={`${adsPeriodPercent}%`}
            tooltipText={t('chatgptAdsPercentTooltip')}
            uniqueId={`ad-slot-badge-${monitorDataId}`}
          />
        )}
      </div>
      <div className="answer-ad-slot__list">
        {features.ads.map((ad, index) => {
          const domain = domainOf(ad.targetUrl ?? ad.advertiserUrl);
          return (
            <a
              key={`ad-slot-${index}`}
              className="answer-ad-slot__card"
              href={getSourceHref(ad.targetUrl ?? ad.advertiserUrl ?? '')}
              target="_blank"
              rel="noopener noreferrer"
            >
              <img
                className="answer-ad-slot__favicon"
                src={`https://favicon.is/${domain}`}
                alt=""
                onError={(e) => ((e.target as HTMLImageElement).style.display = 'none')}
              />
              <div className="answer-ad-slot__card-body">
                <span className="answer-ad-slot__card-title">{ad.title ?? ad.advertiser}</span>
                {domain && <span className="answer-ad-slot__card-domain">{domain}</span>}
              </div>
            </a>
          );
        })}
      </div>
    </div>
  );
};

export default AnswerAdSlot;
